import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';

import { defineMessages, FormattedMessage } from 'react-intl';

import { SCA_RESUME_REQUEST, SCA_RESUME_TICK_COOLDOWN } from '../../constants';
import {
  selectResumeLoading,
  selectResumeCooldown,
} from '../../selectors/sca';

const messages = defineMessages({
  cta: { id: 'sca.resume.cta', defaultMessage: 'Check / Resume bank setup' },
  checking: { id: 'sca.resume.checking', defaultMessage: 'Checking...' },
  cooldown: { id: 'sca.resume.cooldown', defaultMessage: 'You can try again in {seconds}s' },
  hint: { id: 'sca.resume.hint.validity', defaultMessage: 'The resume link is typically valid for a few minutes' },
  err_resume_failed: { id: 'sca.resume.error.resume_failed', defaultMessage: 'Couldn’t resume right now. Try again in a moment.' },
  err_start_over: { id: 'sca.resume.error.start_over', defaultMessage: 'Let’s re-enter your bank details.' },
  err_server_error: { id: 'sca.resume.error.server_error', defaultMessage: 'Something went wrong (server). Please try again.' },
  err_unknown: { id: 'sca.resume.error.unknown', defaultMessage: 'Something went wrong. Please try again.' },
});

export default function ResumeBankSetupButton({ className = '', onBeforeResume }) {
  const dispatch = useDispatch();
  const loading = useSelector(selectResumeLoading);
  const cooldown = useSelector(selectResumeCooldown);

  React.useEffect(() => {
    if (!cooldown) return undefined;
    const id = setInterval(() => dispatch({ type: SCA_RESUME_TICK_COOLDOWN }), 1000);
    return () => clearInterval(id);
  }, [cooldown, dispatch]);

  const showCooldown = !loading && cooldown > 0;
  const disabled = Boolean(loading || cooldown > 0);

  const onClick = () => {
    if (!disabled) {
      if (typeof onBeforeResume === 'function') onBeforeResume();
      dispatch({ type: SCA_RESUME_REQUEST });
    }
  };

  const titleMsg = loading ? messages.checking : messages.cta;

  return (
    <Box className={className} display="flex" flexDirection="column" alignItems="center" textAlign="center">
      <Tooltip
        arrow
        placement="top"
        title={<span><FormattedMessage {...messages.hint} /></span>}
      >
        {/* span wrapper so Tooltip works when Button is disabled */}
        <span>
          <Button
            variant="contained"
            color="primary"
            onClick={onClick}
            disabled={disabled}
            aria-busy={loading ? 'true' : 'false'}
            aria-live="polite"
            mt={1}
          >
            {loading && <CircularProgress size={18} style={{ marginRight: 8 }} />}
            <FormattedMessage {...titleMsg} />
          </Button>
        </span>
      </Tooltip>

      <Box aria-live="polite" mt={1} style={{ fontSize: 12, opacity: 0.8, minHeight: 18 }}>
        {showCooldown && <FormattedMessage {...messages.cooldown} values={{ seconds: cooldown }} />}
      </Box>
    </Box>
  );
}

ResumeBankSetupButton.propTypes = {
  className: PropTypes.string,
  onBeforeResume: PropTypes.func,
};
